import {
  Box,
  Button,
  Group,
  Loader,
  Stack,
  UnstyledButton,
} from '@mantine/core'
import { ReactNode } from 'react'
import { OnboardingDevice } from '../../models/Onboarding/OnboardingDevice'
import { OnboardingLocation } from '../../models/Onboarding/OnboardingLocation'
import {
  HostOnboardingSteps,
  OnboardingWizardPages,
} from '../../utils/constants'
import classes from '../css/FooterOnboardingNavigation.module.css'

interface FooterOnboardingNavigationProps {
  currentStep: number
  handleGoBack: () => void
  handleNextStep: () => void
  handleGoToReview: () => void
  isCurrentStepValid: (currentStep: number) => boolean
  isLoadingNextStep: boolean
  isLoadingBackStep: boolean
  firstStep: HostOnboardingSteps
  progressBar: ReactNode
  location?: OnboardingLocation
  device?: OnboardingDevice | null
  pageName?: OnboardingWizardPages
}

function FooterOnboardingNavigation(props: FooterOnboardingNavigationProps) {
  const {
    currentStep,
    handleGoBack,
    handleNextStep,
    handleGoToReview,
    isCurrentStepValid,
    isLoadingNextStep,
    isLoadingBackStep,
    firstStep,
    progressBar,
    location,
    device,
    pageName,
  } = props

  const isLocationValid = (loc: OnboardingLocation) => {
    return (
      loc.name &&
      loc.description &&
      loc.address &&
      loc.city &&
      loc.state &&
      loc.zipCode &&
      loc.businessHours &&
      loc.imageUrls.length > 0 &&
      loc.footTraffic
    )
  }

  const isDeviceValid = (dev: OnboardingDevice) => {
    return (
      dev.name &&
      dev.imageUrl &&
      dev.screenSize &&
      dev.monthlyPrice &&
      dev.approvalType
    )
  }

  const canShowReview = () => {
    // For onboarding process, require both location and device to be complete
    if (pageName === OnboardingWizardPages.Onboarding) {
      if (!location || !isLocationValid(location)) return false
      if (!device || !isDeviceValid(device)) return false
      return true
    }

    // For location editing, only require location to be complete
    if (pageName === OnboardingWizardPages.ManageLocation) {
      if (!location) return false
      return isLocationValid(location)
    }

    // For device editing, only require device to be complete
    if (pageName === OnboardingWizardPages.ManageDevice) {
      if (!device) return false
      return isDeviceValid(device)
    }

    // Default fallback for backward compatibility
    if (!location) return false
    if (!isLocationValid(location)) return false
    if (device && !isDeviceValid(device)) return false
    return true
  }

  return (
    <Stack
      w='100%'
      pos='sticky'
      style={{
        bottom: 0,
        borderTop: '1px solid lightgray',
      }}
      bg='white'
      pt={30}
    >
      {progressBar}
      <Group
        pt={0}
        p={20}
        pl={{ base: 20, sm: 60 }}
        pr={{ base: 20, sm: 60 }}
        justify='space-between'
      >
        <UnstyledButton
          onClick={handleGoBack}
          className={classes.navigationButton}
          style={{
            visibility: currentStep !== firstStep ? 'visible' : 'hidden',
            height: 40,
            alignContent: 'center',
          }}
          disabled={isLoadingNextStep || isLoadingBackStep}
        >
          <Box h='100%' display='flex' style={{ alignItems: 'center' }}>
            {isLoadingBackStep ? (
              <Loader type='dots' color='black' size='sm' />
            ) : (
              'Back'
            )}
          </Box>
        </UnstyledButton>

        <Group>
          {currentStep !== HostOnboardingSteps.Review && canShowReview() && (
            <Button
              onClick={handleGoToReview}
              disabled={isLoadingNextStep || isLoadingBackStep}
              variant='outline'
              ta='center'
              size='md'
            >
              Review
            </Button>
          )}
          <Button
            onClick={handleNextStep}
            disabled={
              !isCurrentStepValid(currentStep) ||
              isLoadingNextStep ||
              isLoadingBackStep
            }
            ta='center'
            size='md'
          >
            {isLoadingNextStep ? (
              <Loader type='dots' size='md' />
            ) : currentStep === HostOnboardingSteps.Review ? (
              'Done'
            ) : (
              'Next'
            )}
          </Button>
        </Group>
      </Group>
    </Stack>
  )
}

export default FooterOnboardingNavigation
